import {
  Box,
  Checkbox,
  FormControlLabel,
  Link,
  TextField,
  Typography,
  MenuItem,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { selectFormData, updateFormField } from "../../../ReduxStore/UserSlice";
import { PROFESSION_OPTIONS } from "../../../utils/constants";
import SelectField from "../../common/FormFields/SelectField";
import { individualBasicDetailsValidation } from "../../common/ValidationRules/validationRules";
import TermsDialog from "../../common/TermsDialog";
import { FULL_TERMS_CONTENT } from "../../../utils/termsContent";
import { getIndustries, getSectors, getLocationByPincode } from "../../../API/API";
import { toast } from "react-toastify";

const BasicDetailsForm = React.forwardRef((props, ref) => {
  const dispatch = useDispatch();
  const {
    control,
    setValue,
    formState: { errors },
    trigger,
  } = useForm({
    mode: "onChange",
    defaultValues: {
      panNumber: "",
      mobileNo: "",
      email: "",
      profession: "",
      confirmations: false,
      industry: "",
      sector: "",
      state: "",
      city: "",
      zipCode: "",
    },
  });

  React.useImperativeHandle(ref, () => ({
    validateForm: async () => {
      const result = await trigger();
      return result;
    },
  }));

  const formData = useSelector(selectFormData);
  const [industries, setIndustries] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [showPanHelp, setShowPanHelp] = useState(false);
  // const files = useSelector(selectFiles);
  const handleChange = (e) => {
    const { name, value } = e.target;
    dispatch(updateFormField({ field: name, value: value }));
  };
  useEffect(() => {
    setValue("panNumber", formData?.panNumber);
    setValue("mobileNo", formData?.mobileNo);
    setValue("email", formData?.email);
    setValue("profession", formData?.profession);
    setValue("confirmations", formData?.confirmations);
    setValue("industry", formData?.industry);
    setValue("sector", formData?.sector);
    setValue("state", formData?.state);
    setValue("city", formData?.city);
    setValue("zipCode", formData?.zipCode);
  }, []);

  useEffect(() => {
    getIndustries()
      .then((res) => {
        if (res?.statusCode === 200 && Array.isArray(res?.result?.industry)) {
          setIndustries(res.result.industry);
        } else {
          toast.error("Industries data is not in expected format:", res);
          setIndustries([]);
        }
      })
      .catch((error) => {
        toast.error("Error fetching industries:", error);
        setIndustries([]);
      });
  }, []);

  useEffect(() => {
    if (formData.industry) {
      getSectors(formData.industry)
        .then((res) => {
          if (res?.statusCode === 200 && Array.isArray(res?.result?.sector)) {
            setSectors(res.result.sector);
          } else {
            toast.error("Sectors data is not in expected format:", res);
            setSectors([]);
          }
        })
        .catch((error) => {
          toast.error("Error fetching sectors:", error);
          setSectors([]);
        });
    } else {
      setSectors([]);
    }
  }, [formData.industry]);

  useEffect(() => {
    const zipCode = (formData?.zipCode || "").toString().trim();

    const clearStateAndCity = () => {
      if ((formData?.state || "") !== "") {
        dispatch(updateFormField({ field: "state", value: "" }));
        setValue("state", "", { shouldValidate: true, shouldDirty: true });
      }
      if ((formData?.city || "") !== "") {
        dispatch(updateFormField({ field: "city", value: "" }));
        setValue("city", "", { shouldValidate: true, shouldDirty: true });
      }
    };

    const updateField = (field, newValue) => {
      const currentValue = formData?.[field] || "";
      if (newValue && newValue !== currentValue) {
        dispatch(updateFormField({ field, value: newValue }));
        setValue(field, newValue, { shouldValidate: true, shouldDirty: true });
      }
    };

    if (!/^\d{6}$/.test(zipCode)) {
      clearStateAndCity();
      return;
    }

    getLocationByPincode(zipCode)
      .then((response) => {
        const data = (Array.isArray(response) ? response[0] : response) || {};
        const offices = Array.isArray(data?.PostOffice)
          ? data.PostOffice
          : Array.isArray(data?.postOffice)
            ? data.postOffice
            : [];
        if (data?.Status !== "Success" || offices.length === 0) {
          toast.error("No records found for this pincode. Please enter a valid pincode.");
          setValue("zipCode", "", { shouldValidate: true, shouldDirty: true });
          clearStateAndCity();
          return;
        }

        const [office] = offices;
        updateField("state", office?.State || "");
        updateField("city", office?.District || "");
      })
      .catch(() => {
        toast.error("Error fetching location by pincode. Please enter correct pincode.");
        setValue("zipCode", "", { shouldValidate: true, shouldDirty: true });
        clearStateAndCity();
      });
  }, [formData?.zipCode, dispatch, setValue]);

  const [termsDialog, setTermsDialog] = useState({
    open: false,
    title: "",
    content: "",
    highlightSection: "",
  });

  const handleOpenTerms = (type) => {
    setTermsDialog({
      open: true,
      title: "Terms and Conditions",
      content: FULL_TERMS_CONTENT,
      highlightSection: type,
    });
  };

  const handleCloseTerms = () => {
    setTermsDialog({
      open: false,
      title: "",
      content: "",
      highlightSection: "",
    });
  };

  const handleAgreeTerms = async () => {
    setValue("confirmations", true, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true
    });

    handleChange({
      target: {
        name: "confirmations",
        value: true,
      },
    });

    await trigger("confirmations");
  };

  return (
    <Box>
      <Typography variant="body2" sx={{ color: "#666", mb: 3 }}>
        Step 1/4
      </Typography>

      <Box sx={{ mb: 4 }}>
        <Typography variant="body2" fontWeight="medium" sx={{ mb: 2 }}>
          Enter your Permanent Account Number (PAN) in the correct format
          (ABCDE1234F). This is mandatory for KYC verification
          <span style={{ color: "red" }}>*</span>
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Applying for an entity loan?{" "}
          <Link
            component="button"
            onClick={() => setShowPanHelp((prev) => !prev)}
            sx={{ fontWeight: 600, textTransform: "none" }}
          >
            See more
          </Link>
        </Typography>
        {showPanHelp && (
          <Box
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: "8px",
              p: 2,
              backgroundColor: "#fafafa",
              mb: 2,
            }}
          >
            <Typography variant="body2" sx={{ mb: 1 }}>
              • You are currently in the Individual Borrower loan application
              journey.
            </Typography>
            <Typography variant="body2">
              • To proceed with an entity loan application, please select or
              create your profile as an Entity Borrower and continue your
              application from there.
            </Typography>
          </Box>
        )}
        <Controller
          name="panNumber"
          control={control}
          rules={individualBasicDetailsValidation?.panNumber}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              value={formData?.panNumber}
              placeholder="ABCDE1234F"
              slotProps={{ htmlInput: { maxLength: 15 } }}
              error={!!errors.panNumber}
              helperText={errors.panNumber?.message}
              onChange={(e) => {
                e.target.value.replace(/^\s+/, "");
                field.onChange(e);
                handleChange(e);
              }}
            />
          )}
        />
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Enter a valid mobile number for OTP verification. The OTP will be sent
          to this number to proceed<span style={{ color: "red" }}>*</span>
        </Typography>
        <Controller
          name="mobileNo"
          control={control}
          rules={individualBasicDetailsValidation?.mobileNo}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              type="tel"
              value={formData?.mobileNo}
              placeholder="+91-XXXXXXXXXX"
              error={!!errors?.mobileNo}
              slotProps={{ htmlInput: { maxLength: 10, inputMode: "numeric", pattern: "[0-9]*" }, inputLabel: { shrink: true } }}
              helperText={errors?.mobileNo?.message}
              onChange={(e) => {
                e.target.value.replace(/^\s+/, "");
                e.target.value = e.target.value.replace(/[^0-9]/g, "");
                field.onChange(e);
                handleChange(e);
              }}
            />
          )}
        />
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Enter your email address for verification and further communication.
          An OTP will be sent to verify this email ID
          <span style={{ color: "red" }}>*</span>
        </Typography>
        <Controller
          name="email"
          control={control}
          rules={individualBasicDetailsValidation?.email}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              value={formData?.email}
              placeholder="abc@example.com"
              error={!!errors?.email}
              helperText={errors?.email?.message}
              onChange={(e) => {
                e.target.value.replace(/^\s+/, "");
                field.onChange(e);
                handleChange(e);
              }}
            />
          )}
        />
      </Box>

      <Box sx={{ mb: 4 }}>
        <Typography variant="body2" sx={{ mb: 1 }}>
          Select your current profession type
          <span style={{ color: "red" }}>*</span>
        </Typography>
        <Controller
          name="profession"
          control={control}
          rules={individualBasicDetailsValidation.profession}
          render={({ field }) => (
            <SelectField
              {...field}
              value={formData?.profession}
              options={PROFESSION_OPTIONS}
              error={!!errors.profession}
              helperText={errors.profession?.message}
              onChange={(e) => {
                field.onChange(e);
                handleChange(e);
              }}
            />
          )}
        />
      </Box>

      <Box sx={{ mb: 4 }}>
        <Controller
          name="industry"
          control={control}
          rules={{ required: "Please select an industry" }}
          render={({ field }) => (
            <TextField
              {...field}
              select
              fullWidth
              label="Industry"
              required
              error={!!errors.industry}
              helperText={errors.industry?.message}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "4px",
                },
                mb: 2,
              }}
              value={formData?.industry || ""}
              onChange={(e) => {
                field.onChange(e);
                handleChange(e);
                dispatch(updateFormField({ field: "sector", value: "" }));
              }}
            >
              {Array.isArray(industries) && industries.map((industry) => (
                <MenuItem key={industry} value={industry}>
                  {industry}
                </MenuItem>
              ))}
            </TextField>
          )}
        />

        {formData.industry && (
          <Controller
            name="sector"
            control={control}
            rules={{ required: "Please select a sector" }}
            render={({ field }) => (
              <TextField
                {...field}
                select
                fullWidth
                label="Sector"
                required
                error={!!errors.sector}
                helperText={errors.sector?.message}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "4px",
                  },
                  mb: 2,
                }}
                value={formData?.sector || ""}
                onChange={(e) => {
                  field.onChange(e);
                  handleChange(e);
                }}
              >
                {Array.isArray(sectors) && sectors.map((sector) => (
                  <MenuItem key={sector} value={sector}>
                    {sector}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        )}
        <Controller
          name="zipCode"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label="Zip Code"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "4px",
                },
                mb: 2,
              }}
              slotProps={{
                htmlInput: { maxLength: 10 },
                inputLabel: { shrink: true },
              }}
              value={formData?.zipCode || ""}
              onChange={(e) => {
                field.onChange(e);
                handleChange(e);
              }}
            />
          )}
        />

        <Controller
          name="state"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label="State"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "4px",
                },
                mb: 2,
              }}
              value={formData?.state || ""}
              onChange={(e) => {
                field.onChange(e);
                handleChange(e);
              }}
            />
          )}
        />

        <Controller
          name="city"
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              label="City"
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "4px",
                },
                mb: 2,
              }}
              value={formData?.city || ""}
              onChange={(e) => {
                field.onChange(e);
                handleChange(e);
              }}
            />
          )}
        />
      </Box>

      <Box sx={{ mb: 4 }}>
        <Controller
          name="confirmations"
          control={control}
          rules={individualBasicDetailsValidation.confirmations}
          render={({ field }) => (
            <FormControlLabel
              control={
                <Checkbox
                  {...field}
                  checked={field.value || formData?.confirmations}
                  onChange={(e) => {
                    field.onChange(e.target.checked);
                    handleChange({
                      target: {
                        name: "confirmations",
                        value: e.target.checked,
                      },
                    });
                  }}
                />
              }
              label={
                <>
                  I confirm that: <span style={{ color: "red" }}>*</span>
                </>
              }
              error={!!errors.confirmations}
            />
          )}
        />

        <Box sx={{ pl: 4 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            • I/We, declare that we have a clean and satisfactory credit history with no history of default or significant delay in repayment of financial obligations.
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            • I am not classified as a Politically Exposed Person (PEP).
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            • I have reviewed and understood the{" "}
            <Link
              component="button"
              onClick={(e) => {
                e.preventDefault();
                handleOpenTerms("masterAgreement");
              }}
              sx={{ textTransform: "none" }}
            >
              Terms & Condition
            </Link>
            , <Link href="#">Terms of Use</Link>, and{" "}
            <Link href="#">Privacy Policy</Link>. By proceeding with the
            registration, I agree to adhere to and accept the terms outlined.
          </Typography>
          <Typography variant="body2">
            • Additionally, by providing the above information, I withdraw my
            NDNC registration and consent to Dequitec or its authorized
            representatives contacting me via SMS, call, email, or WhatsApp for
            any assistance, without causing me any inconvenience.
          </Typography>
        </Box>
        {errors?.confirmations && (
          <Typography
            variant="caption"
            color="error"
            sx={{ pl: 4, display: "block", mt: 1 }}
          >
            {errors?.confirmations?.message}
          </Typography>
        )}
      </Box>

      <TermsDialog
        open={termsDialog.open}
        onClose={handleCloseTerms}
        title={termsDialog.title}
        content={termsDialog.content}
        highlightSection={termsDialog.highlightSection}
        showAgreeButton={!formData.confirmations}
        onAgree={handleAgreeTerms}
      />
    </Box>
  );
});

export default BasicDetailsForm;
